/**
 * Ros App environment configuration.
 *
 * Local dev: uses defaults (localhost:8787 for reachy-cf).
 * Deployed: overwritten by the deploy script at build time.
 */
// Studio frontend URL — derived DYNAMICALLY from VITE_ENVIRONMENT (injected by every deploy:
// dev / qa / staging / production / dev-<stack>), mirroring Studio's own URL scheme
// (deployment/scripts/deploy_cfn.sh builds https://{subdomain}.{region}.{domain} per environment).
// Previously the deployed fallback was hardcoded to PRODUCTION Studio, so dev/QA/staging Ros builds
// all linked their tools to prod. VITE_STUDIO_URL stays as an explicit override; local dev uses the
// local webpack server.
function studioUrl(): string {
  if (import.meta.env.VITE_STUDIO_URL) return import.meta.env.VITE_STUDIO_URL
  if (import.meta.env.DEV) return 'http://localhost:3001'
  const env: string = import.meta.env.VITE_ENVIRONMENT || 'development'
  if (env === 'production') return 'https://app.us1.r3studio.io' // per deploy_cfn.sh domain construction; confirm at prod rollout
  if (env === 'staging') return 'https://app-staging.us1.r3studio.io'
  // dev, qa, and ephemeral dev-<stack> builds all target Studio Dev (the sandbox stack).
  return 'https://app-r3studio.us1.sandbox-rivaltech.io'
}

// Studio BACKEND (API) origin — a DIFFERENT origin from the Studio frontend in deployed envs
// (deploy_cfn.sh: api-r3studio vs app-r3studio, api-staging vs app-staging, api vs app). Used for
// silent session renewal: POST {STUDIO_API_URL}/api/auth/refresh with credentials — the browser
// attaches Studio's cookies, Studio does the Cognito refresh grant, and returns a fresh access token.
// Same-site notes: locally 3002→3000 is same-site (SameSite ignores ports), so Lax cookies flow.
function studioApiUrl(): string {
  if (import.meta.env.VITE_STUDIO_API_URL) return import.meta.env.VITE_STUDIO_API_URL
  if (import.meta.env.DEV) return 'http://localhost:3000'
  const env: string = import.meta.env.VITE_ENVIRONMENT || 'development'
  if (env === 'production') return 'https://api.us1.r3studio.io' // per deploy_cfn.sh; confirm at prod rollout
  if (env === 'staging') return 'https://api-staging.us1.r3studio.io'
  return 'https://api-r3studio.us1.sandbox-rivaltech.io' // dev, qa, dev-<stack>
}

// ATR is NOT a Studio route — it's an external app with its own per-environment deployments.
// Mirrors Studio's own mapping (web/src/components/AppLayout.tsx getAtrUrl): dev/sandbox → atr-risky,
// staging → atr-reach3, production → atr.us1.r3studio.io.
function atrUrl(): string {
  const env: string = import.meta.env.VITE_ENVIRONMENT || 'development'
  if (env === 'production') return 'https://atr.us1.r3studio.io/'
  if (env === 'staging') return 'https://atr-reach3.us1.sandbox-rivaltech.io/'
  return 'https://atr-risky.us1.sandbox-rivaltech.io/' // local, dev, qa, dev-<stack>
}

export const config = {
  // In dev: empty string = relative paths → Vite proxy handles routing to reachy-cf (no CORS)
  // In prod: full URL to the deployed reachy-cf worker
  API_CF_URL: import.meta.env.VITE_API_CF_URL || '',
  // Where the MCP App single-file bundle is served. Live panels get this from the backend SSE
  // event; the FE only needs it for the session-resume path. Default prod, override locally.
  MCP_APP_URL: import.meta.env.VITE_MCP_APP_URL || 'https://reachy-mcp-app.pages.dev',
  ENVIRONMENT: import.meta.env.VITE_ENVIRONMENT || 'development',
  // Some isolated stacks intentionally have no callback registered with the Rival AS.
  // Their users continue through Studio even if the org-wide OAuth flag is enabled.
  RIVAL_OAUTH_AVAILABLE: import.meta.env.VITE_RIVAL_OAUTH_AVAILABLE !== 'false',
  // Ros Next and deployed personal stacks use Better Auth email codes instead
  // of depending on Studio or the Rival authorization service.
  // Keep reading the existing deployment variable to avoid a stack configuration migration.
  EMAIL_CODE_AUTH_ENABLED: import.meta.env.VITE_MAGIC_LINK_AUTH_ENABLED === 'true',
  VERSION: import.meta.env.VITE_VERSION || 'dev',
  BUILD_DATE: import.meta.env.VITE_BUILD_DATE || new Date().toISOString(),
  // Studio web app — environment-derived (see studioUrl above).
  STUDIO_URL: studioUrl(),
  // Studio backend/API — environment-derived (see studioApiUrl above). Session renewal target.
  STUDIO_API_URL: studioApiUrl(),
  // ATR external app — environment-derived (see atrUrl above).
  ATR_URL: atrUrl(),
}

/** Known source apps that link to Ros — return URLs are config-based, not from URL params */
export const KNOWN_SOURCES: Record<string, { label: string; url: string }> = {
  studio: { label: 'Studio', url: config.STUDIO_URL },
  rival: { label: 'Rival', url: 'https://app.rivaltech.com' },
}
